import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Pencil, Users, Wallet, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MESI_LABEL = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const fmtEuro = (n) => (Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const meseLabel = (k) => {
  if (!k || !/^\d{4}-\d{2}$/.test(k)) return k || '—';
  const [y, m] = k.split('-');
  return `${MESI_LABEL[parseInt(m, 10) - 1] || m} ${y}`;
};

export default function SociPage() {
  const [soci, setSoci] = useState([]);
  const [compensi, setCompensi] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socioDialogOpen, setSocioDialogOpen] = useState(false);
  const [socioEdit, setSocioEdit] = useState(null);
  const [socioForm, setSocioForm] = useState({ name: '', codice_fiscale: '', quota_percentuale: 0, ruolo: 'socio', email: '', telefono: '', user_id: '', note: '' });

  const [compDialogOpen, setCompDialogOpen] = useState(false);
  const [compEdit, setCompEdit] = useState(null);
  const [compForm, setCompForm] = useState({ socio_id: '', mese: '', importo_lordo: 0, importo_netto: 0, contributi_inps: 0, ritenuta_acconto: 0, altre_ritenute: 0, stato: 'da_pagare', data_pagamento: '', causale: '', modalita_pagamento: '', note: '' });

  const [filtroSocio, setFiltroSocio] = useState('tutti');
  const [filtroAnno, setFiltroAnno] = useState(String(new Date().getFullYear()));

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchCompensi(); }, [filtroSocio, filtroAnno]);

  const fetchAll = async () => {
    try {
      const [sr, ur] = await Promise.all([
        axios.get(`${API}/soci`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSoci(sr.data || []);
      setUsers((ur.data || []).filter(u => u.role === 'admin' || u.role === 'operaio'));
      await fetchCompensi();
    } catch (err) {
      toast.error('Errore caricamento soci');
    } finally { setLoading(false); }
  };

  const fetchCompensi = async () => {
    try {
      const params = [];
      if (filtroSocio && filtroSocio !== 'tutti') params.push(`socio_id=${filtroSocio}`);
      if (filtroAnno) params.push(`anno=${filtroAnno}`);
      const q = params.length ? '?' + params.join('&') : '';
      const r = await axios.get(`${API}/compensi-soci${q}`, { withCredentials: true });
      setCompensi(r.data || []);
    } catch { /* silent */ }
  };

  const openSocioNew = () => { setSocioEdit(null); setSocioForm({ name: '', codice_fiscale: '', quota_percentuale: 0, ruolo: 'socio', email: '', telefono: '', user_id: '', note: '' }); setSocioDialogOpen(true); };
  const openSocioEdit = (s) => {
    setSocioEdit(s);
    setSocioForm({
      name: s.name || '', codice_fiscale: s.codice_fiscale || '',
      quota_percentuale: s.quota_percentuale || 0, ruolo: s.ruolo || 'socio',
      email: s.email || '', telefono: s.telefono || '', user_id: s.user_id || '', note: s.note || '',
    });
    setSocioDialogOpen(true);
  };
  const saveSocio = async () => {
    if (!socioForm.name.trim()) { toast.error('Nome obbligatorio'); return; }
    try {
      const payload = { ...socioForm, quota_percentuale: Number(socioForm.quota_percentuale) || 0 };
      if (socioEdit) await axios.put(`${API}/soci/${socioEdit.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/soci`, payload, { withCredentials: true });
      toast.success(socioEdit ? 'Socio aggiornato' : 'Socio creato');
      setSocioDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio socio');
    }
  };
  const deleteSocio = async (s) => {
    if (!window.confirm(`Eliminare il socio "${s.name}"? I compensi storici resteranno visibili ma non sarà più possibile aggiungere nuovi compensi.`)) return;
    try {
      await axios.delete(`${API}/soci/${s.id}`, { withCredentials: true });
      toast.success('Socio eliminato');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const openCompNew = () => {
    const oggi = new Date();
    const mese = `${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, '0')}`;
    setCompEdit(null);
    setCompForm({ socio_id: soci[0]?.id || '', mese, importo_lordo: 0, importo_netto: 0, contributi_inps: 0, ritenuta_acconto: 0, altre_ritenute: 0, stato: 'da_pagare', data_pagamento: '', causale: '', modalita_pagamento: '', note: '' });
    setCompDialogOpen(true);
  };
  const openCompEdit = (c) => {
    setCompEdit(c);
    setCompForm({
      socio_id: c.socio_id || '', mese: c.mese || '',
      importo_lordo: c.importo_lordo || 0, importo_netto: c.importo_netto || 0,
      contributi_inps: c.contributi_inps || 0, ritenuta_acconto: c.ritenuta_acconto || 0, altre_ritenute: c.altre_ritenute || 0,
      stato: c.stato || 'da_pagare', data_pagamento: c.data_pagamento || '',
      causale: c.causale || '', modalita_pagamento: c.modalita_pagamento || '', note: c.note || '',
    });
    setCompDialogOpen(true);
  };
  const saveComp = async () => {
    if (!compForm.socio_id) { toast.error('Seleziona un socio'); return; }
    if (!/^\d{4}-\d{2}$/.test(compForm.mese)) { toast.error('Mese in formato YYYY-MM'); return; }
    try {
      const payload = {
        ...compForm,
        importo_lordo: Number(compForm.importo_lordo) || 0,
        importo_netto: Number(compForm.importo_netto) || 0,
        contributi_inps: Number(compForm.contributi_inps) || 0,
        ritenuta_acconto: Number(compForm.ritenuta_acconto) || 0,
        altre_ritenute: Number(compForm.altre_ritenute) || 0,
      };
      if (compEdit) await axios.put(`${API}/compensi-soci/${compEdit.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/compensi-soci`, payload, { withCredentials: true });
      toast.success(compEdit ? 'Compenso aggiornato' : 'Compenso registrato');
      setCompDialogOpen(false);
      fetchCompensi();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio compenso');
    }
  };
  const deleteComp = async (c) => {
    if (!window.confirm(`Eliminare il compenso di ${c.socio_name} · ${meseLabel(c.mese)}?`)) return;
    try {
      await axios.delete(`${API}/compensi-soci/${c.id}`, { withCredentials: true });
      toast.success('Compenso eliminato');
      fetchCompensi();
    } catch { toast.error('Errore'); }
  };

  const totali = useMemo(() => {
    return {
      lordo: compensi.reduce((s, c) => s + (Number(c.importo_lordo) || 0), 0),
      netto: compensi.reduce((s, c) => s + (Number(c.importo_netto) || 0), 0),
      contributi: compensi.reduce((s, c) => s + (Number(c.contributi_inps) || 0), 0),
      daPagare: compensi.filter(c => c.stato === 'da_pagare').reduce((s, c) => s + (Number(c.importo_netto) || 0), 0),
    };
  }, [compensi]);

  const anniDisponibili = useMemo(() => {
    const currY = new Date().getFullYear();
    const set = new Set([currY, currY - 1, currY - 2]);
    compensi.forEach(c => { if (c.mese && /^\d{4}/.test(c.mese)) set.add(parseInt(c.mese.slice(0, 4), 10)); });
    return Array.from(set).sort((a, b) => b - a);
  }, [compensi]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="soci-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Soci e Compensi</h1>
          <p className="text-slate-500 mt-1">Anagrafica soci della società e liquidazioni mensili dei compensi</p>
        </div>
      </div>

      <Tabs defaultValue="compensi">
        <TabsList>
          <TabsTrigger value="compensi" data-testid="tab-compensi"><Wallet className="h-4 w-4 mr-1.5" />Compensi</TabsTrigger>
          <TabsTrigger value="anagrafica" data-testid="tab-anagrafica"><Users className="h-4 w-4 mr-1.5" />Anagrafica Soci</TabsTrigger>
        </TabsList>

        {/* ============ COMPENSI ============ */}
        <TabsContent value="compensi" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-blue-200 bg-blue-50/50"><CardContent className="p-4"><p className="text-[10px] text-blue-600 uppercase font-medium">Totale Lordo</p><p className="text-2xl font-bold text-blue-700" data-testid="tot-lordo">{fmtEuro(totali.lordo)}</p></CardContent></Card>
            <Card className="border-emerald-200 bg-emerald-50/50"><CardContent className="p-4"><p className="text-[10px] text-emerald-600 uppercase font-medium">Totale Netto</p><p className="text-2xl font-bold text-emerald-700" data-testid="tot-netto">{fmtEuro(totali.netto)}</p></CardContent></Card>
            <Card className="border-slate-200 bg-slate-50/50"><CardContent className="p-4"><p className="text-[10px] text-slate-600 uppercase font-medium">Contributi INPS</p><p className="text-2xl font-bold text-slate-700" data-testid="tot-contributi">{fmtEuro(totali.contributi)}</p></CardContent></Card>
            <Card className="border-amber-200 bg-amber-50/50"><CardContent className="p-4"><p className="text-[10px] text-amber-600 uppercase font-medium">Da Pagare</p><p className="text-2xl font-bold text-amber-700" data-testid="tot-da-pagare">{fmtEuro(totali.daPagare)}</p></CardContent></Card>
          </div>

          <Card>
            <CardContent className="p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Select value={filtroSocio} onValueChange={setFiltroSocio}>
                  <SelectTrigger className="w-[200px]" data-testid="filtro-socio"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutti i soci</SelectItem>
                    {soci.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filtroAnno} onValueChange={setFiltroAnno}>
                  <SelectTrigger className="w-[130px]" data-testid="filtro-anno"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {anniDisponibili.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={openCompNew} disabled={soci.length === 0} className="ml-auto bg-slate-900 hover:bg-slate-800" data-testid="new-compenso-btn">
                  <Plus className="h-4 w-4 mr-1.5" />Nuovo Compenso
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Socio</TableHead>
                    <TableHead>Mese</TableHead>
                    <TableHead className="text-right">Lordo</TableHead>
                    <TableHead className="text-right">Contributi</TableHead>
                    <TableHead className="text-right">Netto</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Pagato il</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {compensi.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">
                      {soci.length === 0 ? 'Crea prima un socio in "Anagrafica Soci", poi torna qui per registrare i compensi.' : 'Nessun compenso registrato con il filtro corrente.'}
                    </TableCell></TableRow>
                  ) : compensi.map(c => (
                    <TableRow key={c.id} data-testid={`compenso-row-${c.id}`}>
                      <TableCell className="font-medium">{c.socio_name}</TableCell>
                      <TableCell>{meseLabel(c.mese)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtEuro(c.importo_lordo)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-500">{fmtEuro(c.contributi_inps)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-700">{fmtEuro(c.importo_netto)}</TableCell>
                      <TableCell>
                        {c.stato === 'pagato'
                          ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">Pagato</Badge>
                          : <Badge className="bg-amber-100 text-amber-800 border-amber-200" variant="outline">Da Pagare</Badge>}
                      </TableCell>
                      <TableCell className="text-slate-600">{c.data_pagamento || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openCompEdit(c)} data-testid={`edit-compenso-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => deleteComp(c)} data-testid={`delete-compenso-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ ANAGRAFICA ============ */}
        <TabsContent value="anagrafica" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openSocioNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-socio-btn"><Plus className="h-4 w-4 mr-1.5" />Nuovo Socio</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Nome</TableHead>
                    <TableHead>Ruolo</TableHead>
                    <TableHead className="text-right">Quota %</TableHead>
                    <TableHead>Codice Fiscale</TableHead>
                    <TableHead>Account collegato</TableHead>
                    <TableHead>Contatti</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {soci.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">Nessun socio. Crea il primo con il pulsante &quot;Nuovo Socio&quot;.</TableCell></TableRow>
                  ) : soci.map(s => (
                    <TableRow key={s.id} data-testid={`socio-row-${s.id}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{s.ruolo?.replace('_', ' ') || 'socio'}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{(s.quota_percentuale || 0).toFixed(2)}%</TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{s.codice_fiscale || '—'}</TableCell>
                      <TableCell>
                        {s.user_email
                          ? <div className="flex items-center gap-1 text-sm"><Link2 className="h-3 w-3 text-emerald-500" /><span className="text-slate-700">{s.user_email}</span><Badge variant="outline" className="text-[10px]">{s.user_role}</Badge></div>
                          : <span className="text-slate-400 text-xs">nessuno</span>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{s.email && <div>{s.email}</div>}{s.telefono && <div>{s.telefono}</div>}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openSocioEdit(s)} data-testid={`edit-socio-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => deleteSocio(s)} data-testid={`delete-socio-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ SOCIO DIALOG ============ */}
      <Dialog open={socioDialogOpen} onOpenChange={setSocioDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="socio-dialog">
          <DialogHeader><DialogTitle>{socioEdit ? 'Modifica socio' : 'Nuovo socio'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome *</Label>
              <Input value={socioForm.name} onChange={e => setSocioForm({...socioForm, name: e.target.value})} data-testid="socio-form-name" />
            </div>
            <div>
              <Label>Codice Fiscale</Label>
              <Input value={socioForm.codice_fiscale} onChange={e => setSocioForm({...socioForm, codice_fiscale: e.target.value.toUpperCase()})} />
            </div>
            <div>
              <Label>Quota %</Label>
              <Input type="number" step="0.01" min="0" max="100" value={socioForm.quota_percentuale} onChange={e => setSocioForm({...socioForm, quota_percentuale: e.target.value})} />
            </div>
            <div>
              <Label>Ruolo</Label>
              <Select value={socioForm.ruolo} onValueChange={v => setSocioForm({...socioForm, ruolo: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="socio">Socio</SelectItem>
                  <SelectItem value="amministratore">Amministratore</SelectItem>
                  <SelectItem value="socio_lavoratore">Socio lavoratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account collegato</Label>
              <Select value={socioForm.user_id || '__none__'} onValueChange={v => setSocioForm({...socioForm, user_id: v === '__none__' ? '' : v})}>
                <SelectTrigger data-testid="socio-form-user-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nessun account —</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={socioForm.email} onChange={e => setSocioForm({...socioForm, email: e.target.value})} />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={socioForm.telefono} onChange={e => setSocioForm({...socioForm, telefono: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Note</Label>
              <Textarea rows={2} value={socioForm.note} onChange={e => setSocioForm({...socioForm, note: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSocioDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveSocio} data-testid="socio-form-save">{socioEdit ? 'Salva modifiche' : 'Crea socio'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ COMPENSO DIALOG ============ */}
      <Dialog open={compDialogOpen} onOpenChange={setCompDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="compenso-dialog">
          <DialogHeader><DialogTitle>{compEdit ? 'Modifica compenso' : 'Nuovo compenso'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Socio *</Label>
              <Select value={compForm.socio_id} onValueChange={v => setCompForm({...compForm, socio_id: v})}>
                <SelectTrigger data-testid="comp-form-socio"><SelectValue placeholder="Seleziona socio" /></SelectTrigger>
                <SelectContent>{soci.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mese (YYYY-MM) *</Label>
              <Input placeholder="2026-07" value={compForm.mese} onChange={e => setCompForm({...compForm, mese: e.target.value})} data-testid="comp-form-mese" />
            </div>
            <div>
              <Label>Stato</Label>
              <Select value={compForm.stato} onValueChange={v => setCompForm({...compForm, stato: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="da_pagare">Da pagare</SelectItem>
                  <SelectItem value="pagato">Pagato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Importo lordo</Label>
              <Input type="number" step="0.01" value={compForm.importo_lordo} onChange={e => setCompForm({...compForm, importo_lordo: e.target.value})} data-testid="comp-form-lordo" />
            </div>
            <div>
              <Label>Contributi INPS</Label>
              <Input type="number" step="0.01" value={compForm.contributi_inps} onChange={e => setCompForm({...compForm, contributi_inps: e.target.value})} />
            </div>
            <div>
              <Label>Ritenuta d&apos;acconto</Label>
              <Input type="number" step="0.01" value={compForm.ritenuta_acconto} onChange={e => setCompForm({...compForm, ritenuta_acconto: e.target.value})} />
            </div>
            <div>
              <Label>Altre ritenute</Label>
              <Input type="number" step="0.01" value={compForm.altre_ritenute} onChange={e => setCompForm({...compForm, altre_ritenute: e.target.value})} />
            </div>
            <div>
              <Label>Importo netto</Label>
              <Input type="number" step="0.01" value={compForm.importo_netto} onChange={e => setCompForm({...compForm, importo_netto: e.target.value})} data-testid="comp-form-netto" />
            </div>
            <div>
              <Label>Data pagamento</Label>
              <Input type="date" value={compForm.data_pagamento} onChange={e => setCompForm({...compForm, data_pagamento: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Modalità pagamento</Label>
              <Input placeholder="es. Bonifico, Cassa..." value={compForm.modalita_pagamento} onChange={e => setCompForm({...compForm, modalita_pagamento: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Causale</Label>
              <Input placeholder="es. Compenso amministratore 07/2026" value={compForm.causale} onChange={e => setCompForm({...compForm, causale: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Note</Label>
              <Textarea rows={2} value={compForm.note} onChange={e => setCompForm({...compForm, note: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveComp} data-testid="comp-form-save">{compEdit ? 'Salva modifiche' : 'Crea compenso'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
